import { MetricType } from 'components/common/types/DrilldownReports.types';
import { RowGroupColumn } from 'customHooks/useRowGroupColumns';
import { Environment } from 'components/common/types/Deal.types';
import { DirectSalesMediaType } from 'components/common/types/DirectSalesCampaign.types';
import { NullableCodeName } from 'components/common/types/CodeName.types';
import { GridOptions, GridReadyEvent } from 'ag-grid-community';
import { LocationItem, FormatItem, NetworkItem, FrameList } from './AddFilter/AddFilter.types';

export type AvailabilityReportFilter = {
  startDate?: string;
  endDate?: string;
  mediaType?: DirectSalesMediaType;
  channels?: Environment[];
  locations?: LocationItem[];
  formats?: FormatItem[];
  networks?: NetworkItem[];
  frameList?: FrameList;
};

export type MandatoryAvailabilityReportFilter = {
  market: string;
  startDate: string;
  endDate: string;
  assetTypes: DirectSalesMediaType[];
  channelCodes: string[];
};

export type OptionalAvailabilityReportFilter = {
  locations?: string[];
  includeProductFormatCodes?: string[];
};

export type AvailabilityReportFiltersProps = {
  selectedFilters: AvailabilityReportFilter;
  updateFilters: (filters: Partial<AvailabilityReportFilter>) => void;
  clearFilters: () => void;
  isDisabled?: boolean;
};

export enum AvailabilityGranularity {
  WEEK = 'WEEK',
  DAY = 'DAY',
  HOUR = 'HOUR',
}

export interface AvailabilityMetadata {
  market: string;
  startDate: string;
  endDate: string;
  channels: string[];
  assetTypes: string[];
  granularity: AvailabilityGranularity;
}

export interface AvailabilityFrameLocationFields {
  districtName?: string;
  districtCode?: string;
  townName?: string;
  townCode?: string;
  countyName?: string;
  countyCode?: string;
  countryName?: string;
  country?: string;
  postcode?: string;
  tvArea?: string;
}

export interface AvailabilityFrameAttributes extends AvailabilityFrameLocationFields {
  assetType: string;
  channel: string;
  productFormatCode: string;
  productFormatName?: string;
  businessAreaCode: string;
  businessAreaName?: string;
  revenueStreamCode: string;
  revenueStreamName?: string;
  sizeCode: string;
  frameSizeName?: string;
  tags: string[] | null;
  networkCodes?: string[] | null;
  networkNames?: string[] | null;
}

export interface AvailabilityData {
  availableImpressions?: number[];
  availableHits?: boolean[];
  availableSot?: number[];
}

export interface AvailabilityTotals {
  totalAvailableImpressions?: number;
  totalAvailableHits?: number;
  avgAvailableSot?: number;
}

export interface AvailabilityFrame {
  frameId: string;
  attributes: AvailabilityFrameAttributes;
  availability: AvailabilityData;
  totals: AvailabilityTotals;
}

export interface AvailabilitySummary {
  frameCount: number;
  totalAvailableImpressions?: number;
  totalAvailableHits?: number;
  avgAvailableSot?: number;
  availableImpressions?: number[];
  availableHits?: boolean[];
  availableAvgSot?: number[];
}

export interface AvailabilityReportData {
  metadata: AvailabilityMetadata;
  frames: AvailabilityFrame[];
  summary: AvailabilitySummary;
}

export interface AvailabilityDisplayFrame {
  frameId: string;
  productFormat?: NullableCodeName;
  businessArea?: NullableCodeName;
  revenueStream?: NullableCodeName;
  size?: NullableCodeName;
  district?: NullableCodeName;
  network?: NullableCodeName;
  town?: NullableCodeName;
  total: number;
  availability: AvailabilityDisplayWeek[];
}

export interface AvailabilityDisplayWeek {
  availabilityWeek: number;
  total: number;
  days?: AvailabilityDisplayDay[];
}

export interface AvailabilityDisplayDay {
  date: string;
  total: number;
  hours?: AvailabilityDisplayHour[];
}

export interface AvailabilityDisplayHour {
  hour: number;
  total: number;
}

export type TransformedAvailabilityRequest = {
  filter: {
    market: string;
    assetTypes: string[];
    startDate: string;
    endDate: string;
    channelCodes: string[];
    locations?: string[];
    includeProductFormatCodes?: string[];
  };
  granularity: AvailabilityGranularity;
  metric: string;
  mode: string;
};

export type TransformedAvailabilityExportRequest = Omit<TransformedAvailabilityRequest, 'mode'>;

export type ChangeMetricTypeHandler = (metricType: MetricType) => void;

export interface AvailabilityReportTableParams {
  gridOptions: GridOptions;
  onGridReady: (event: GridReadyEvent<AvailabilityDisplayFrame[]>) => void;
  rowData: AvailabilityDisplayFrame[];
  pinnedBottomRowData?: AvailabilityDisplayFrame[];
}

export type UseAvailabilityReport = ({ onDataFetched }: { onDataFetched?: () => void }) => {
  availabilityReportData?: AvailabilityReportData;
  availabilityReportTableParams: AvailabilityReportTableParams;
  handleChangeMetricType: ChangeMetricTypeHandler;
  handleFetchAvailabilityReport: (filters: AvailabilityReportFilter) => void;
  clearAvailabilityReportData: () => void;
  exportGridAsCsv: () => void;
  exportGridAsExcel: () => void;
  handleExportReport: (filters: AvailabilityReportFilter) => void;
  isLoading: boolean;
  isTableLoading: boolean;
  isExportLoading: boolean;
  selectedMetricType: MetricType;
  selectedRowGroupColumns: RowGroupColumn[];
  handleToggleRowGroupColumn: (rowGroupColumns: RowGroupColumn[]) => void;
  resetRowGroupColumns: () => void;
  rowGroupColumns: RowGroupColumn[];
};
